import { useState, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { RadioButton } from '@/src/elements/radio/RadioButton'
import { Typography } from '@/src/elements/text/Typography'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { useCommunityJoinSteps } from '@/src/features/community/ui/hooks/useCommunityJoinSteps'
import { FraternityOnboardingWrapper } from '@/src/features/fraternity/ui/components/FraternityOnboardingWrapper'
import { useSettingsMutation } from '@/src/features/settings/services/useSettingsMutation'
import { GENDER } from '@/src/features/user/models/user.enum'
import { ICON } from '@/src/general/enums/icons'
import { useOnChange } from '@/src/general/hooks/useOnChange'
import { useTranslate } from '@/src/lang/useTranslate'

type TFraternityGenderScreenProps = TIdSlugParam & TSearchParamsParam

export const FraternityGenderScreen = ({ idSlug, searchParams }: TFraternityGenderScreenProps): ReactNode => {
  const { t } = useTranslate()
  const { data: me } = useMeQuery()
  const joinSteps = useCommunityJoinSteps({ idSlug, searchParams, stepName: 'gender' })
  const { goToNextStep } = joinSteps
  const [gender, setGender] = useState<GENDER | null>(me?.gender ?? null)
  const { mutateAsync, isPending: isSaving } = useSettingsMutation()

  useOnChange(me?.gender, () => {
    if (me?.gender) setGender(me.gender)
  })

  const handleValidate = async () => {
    if (!gender) return
    await mutateAsync({ gender })
    goToNextStep()
  }

  return (
    <FraternityOnboardingWrapper
      joinSteps={joinSteps}
      currentStep="gender"
      title={<FormattedMessage id="fraternity.gender.title" />}
      footer={
        <Button
          colors="primary"
          variant="solid"
          fullWidth
          isDisabled={!gender}
          isLoading={isSaving}
          onPress={handleValidate}
        >
          <FormattedMessage id="word.continue" />
        </Button>
      }
    >
      <View className="gap-4">
        <Typography className="pl-4">
          <FormattedMessage id="fraternity.gender.question" />
        </Typography>
        <View className="gap-3">
          <RadioButton
            icon={ICON.FEMALE}
            selected={gender === GENDER.F}
            label={t('words.a-woman')}
            onPress={() => setGender(GENDER.F)}
            classNames={{ iconLeft: 'text-accents-pink-default' }}
          />
          <RadioButton
            icon={ICON.MALE}
            selected={gender === GENDER.M}
            label={t('words.a-man')}
            onPress={() => setGender(GENDER.M)}
            classNames={{ iconLeft: 'text-accents-teal-default' }}
          />
        </View>
      </View>
    </FraternityOnboardingWrapper>
  )
}
